/**
 * VersionCompareOverlay - an inline redline between an older snapshot and the
 * current document.
 *
 * Version history previews a whole snapshot; this shows what actually CHANGED
 * between that snapshot and the live document, as a git-style redline (removed
 * lines struck through in red, added lines in green, and small edits shown as
 * word-level changes on one line). Self-contained: its own backdrop and close,
 * and it does its own two content fetches - so it doesn't touch the editor.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiXLight, PiArrowCounterClockwiseLight } from 'react-icons/pi';
import { useAppDispatch } from '../../../store';
import {
  fetchDraftingVersionContent,
  DraftingKind,
} from '../../../store/slices/draftingSlice';
import Button from '../../shared/TailwindComponents/Button';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import { diffText, textsIdentical, DiffLine } from '../../../utils/textDiff';

interface Props {
  kind: DraftingKind;
  uuid: string;
  oldVersionId: string;
  currentVersionId: string;
  oldLabel: string;
  restoring: boolean;
  onRestore: () => void;
  onClose: () => void;
}

const VersionCompareOverlay: React.FC<Props> = ({
  kind, uuid, oldVersionId, currentVersionId, oldLabel, restoring, onRestore, onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [oldText, setOldText] = useState('');
  const [newText, setNewText] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [oldRes, newRes] = await Promise.all([
          dispatch(fetchDraftingVersionContent({ kind, uuid, versionId: oldVersionId })),
          dispatch(fetchDraftingVersionContent({ kind, uuid, versionId: currentVersionId })),
        ]);
        if (cancelled) return;
        if (fetchDraftingVersionContent.fulfilled.match(oldRes)
          && fetchDraftingVersionContent.fulfilled.match(newRes)) {
          setOldText(oldRes.payload.content || '');
          setNewText(newRes.payload.content || '');
        } else {
          setError(t('Could not load the versions to compare.'));
        }
      } catch {
        if (!cancelled) setError(t('Could not load the versions to compare.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [dispatch, kind, uuid, oldVersionId, currentVersionId, t]);

  const lines: DiffLine[] = useMemo(
    () => (loading || error ? [] : diffText(oldText, newText)),
    [loading, error, oldText, newText],
  );
  const identical = !loading && !error && textsIdentical(oldText, newText);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/20 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div className="relative z-10 flex max-h-[85vh] w-full max-w-4xl flex-col rounded-2xl glass shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-hairline px-5 py-3.5">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-fg-strong">{t('Compare with current')}</h3>
            <p className="truncate text-xs text-fg-muted">
              {t('{{old}} → Current version', { old: oldLabel })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral"
              size="small"
              disabled={restoring}
              isLoading={restoring}
              icon={restoring ? undefined : <PiArrowCounterClockwiseLight size={13} />}
              onClick={onRestore}
            >
              {t('Restore this version')}
            </Button>
            <Button
              variant="neutral"
              size="small"
              onClick={onClose}
              ariaLabel={t('Close')}
              icon={<PiXLight size={16} />}
            />
          </div>
        </div>

        {/* Legend */}
        {!loading && !error && !identical && (
          <div className="flex items-center gap-4 border-b border-hairline-soft px-5 py-2 text-[11px] text-fg-muted">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-success-500/70" /> {t('Added (now)')}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-danger-500/70" /> {t('Removed (was in {{old}})', { old: oldLabel })}
            </span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-5 py-4">
          {loading && <Skeleton variant="list" rows={8} />}
          {!loading && error && (
            <p className="py-6 text-center text-sm text-danger-600">{error}</p>
          )}
          {!loading && !error && identical && (
            <p className="py-6 text-center text-sm text-fg-muted">
              {t('No differences — this version is identical to the current document.')}
            </p>
          )}
          {!loading && !error && !identical && (
            <pre className="whitespace-pre-wrap break-words font-mono text-[12.5px] leading-relaxed">
              {lines.map((ln, i) => {
                if (ln.segments) {
                  return (
                    <div key={i} className="px-1">
                      {ln.segments.map((s, j) => (
                        <span
                          key={j}
                          className={
                            s.op === 'add'
                              ? 'bg-success-500/15 text-success-800 dark:text-success-300'
                              : s.op === 'del'
                                ? 'bg-danger-500/15 text-danger-800 line-through dark:text-danger-300'
                                : ''
                          }
                        >
                          {s.text}
                        </span>
                      ))}
                    </div>
                  );
                }
                if (ln.op === 'add') {
                  return (
                    <div key={i} className="bg-success-500/10 px-1 text-success-800 dark:text-success-300">
                      <span className="select-none opacity-60">+ </span>{ln.text}
                    </div>
                  );
                }
                if (ln.op === 'del') {
                  return (
                    <div key={i} className="bg-danger-500/10 px-1 text-danger-800 line-through dark:text-danger-300">
                      <span className="select-none no-underline opacity-60">− </span>{ln.text}
                    </div>
                  );
                }
                return (
                  <div key={i} className="px-1 text-fg-muted">
                    <span className="select-none opacity-40">&nbsp;&nbsp;</span>{ln.text}
                  </div>
                );
              })}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default VersionCompareOverlay;
